import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { motion } from "framer-motion";
import { ExternalLink, Mail, User } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./MisAvisosLaborales.module.scss";
import {
  fetchMyJobRequests,
  fetchMyJobRequestPostulations,
  clearMyJobRequestPostulations,
} from "../../../../store/actions";
import ProfileEmptyState from "../ProfileEmptyState/ProfileEmptyState.tsx";
import RegisteredUserProfileLoader from "../../../RegisteredUsers/RegisteredUsersSkeleton/RegisteredUserProfileLoader";
import type { MyJobRequestItem } from "../../../../types/jobRequest.ts";

interface MisAvisosLaboralesProps {
  myJobRequests: MyJobRequestItem[];
  myJobRequestsLoading: boolean;
  myJobRequestsError: string | null;
  selectedMyJobRequest: { id: number; description?: string | null; activeUntil?: string; contactMethod?: string; alternativeContact?: string | null } | null;
  myJobRequestPostulations: Array<{
    id: number;
    appliedAt: string;
    coverLetter: string | null;
    cvUrl: string | null;
    applicant: {
      name?: string;
      last_name?: string;
      username?: string;
      phone?: string;
      identity_validated?: boolean | null;
    } | null;
  }>;
  myJobRequestPostulationsLoading: boolean;
  myJobRequestPostulationsError: string | null;
  fetchMyJobRequests: () => void;
  fetchMyJobRequestPostulations: (id: number) => void;
  clearMyJobRequestPostulations: () => void;
  setInfoReady: (ready: boolean) => void;
}

type PostulationFilter = "all" | "with" | "without";
type SortOrder = "recent" | "oldest" | "most_postulations";

const getPostulationCount = (jobRequest: MyJobRequestItem) =>
  jobRequest.postulationCount ?? 0;

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-AR");
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const MisAvisosLaborales: React.FC<MisAvisosLaboralesProps> = ({
  myJobRequests,
  myJobRequestsLoading,
  myJobRequestsError,
  selectedMyJobRequest,
  myJobRequestPostulations,
  myJobRequestPostulationsLoading,
  myJobRequestPostulationsError,
  fetchMyJobRequests,
  fetchMyJobRequestPostulations,
  clearMyJobRequestPostulations,
  setInfoReady,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedId = selectedMyJobRequest?.id ?? null;
  const [postulationFilter, setPostulationFilter] = useState<PostulationFilter>("all");
  const [sortOrder, setSortOrder] = useState<SortOrder>("recent");
  const appliedDeepLinkJobRequestRef = useRef<number | null>(null);

  const deepLinkJobRequestId = useMemo(() => {
    const raw = searchParams.get("jobRequestId");
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }, [searchParams]);

  useEffect(() => {
    fetchMyJobRequests();
    return () => {
      clearMyJobRequestPostulations();
    };
  }, [fetchMyJobRequests, clearMyJobRequestPostulations]);

  useEffect(() => {
    if (!myJobRequestsLoading && myJobRequests !== undefined) {
      setInfoReady(true);
    }
  }, [myJobRequestsLoading, myJobRequests, setInfoReady]);

  useEffect(() => {
    if (deepLinkJobRequestId == null || myJobRequestsLoading) return;
    if (appliedDeepLinkJobRequestRef.current === deepLinkJobRequestId) return;
    const exists = (myJobRequests || []).some((jr) => jr.id === deepLinkJobRequestId);
    if (!exists) return;
    appliedDeepLinkJobRequestRef.current = deepLinkJobRequestId;
    fetchMyJobRequestPostulations(deepLinkJobRequestId);
  }, [
    deepLinkJobRequestId,
    myJobRequestsLoading,
    myJobRequests,
    fetchMyJobRequestPostulations,
  ]);

  const totalCount = myJobRequests?.length ?? 0;

  const withPostulationsCount = useMemo(
    () => (myJobRequests || []).filter((jr) => getPostulationCount(jr) > 0).length,
    [myJobRequests]
  );

  const filteredJobRequests = useMemo(() => {
    let list = [...(myJobRequests || [])];
    if (postulationFilter === "with") {
      list = list.filter((jr) => getPostulationCount(jr) > 0);
    } else if (postulationFilter === "without") {
      list = list.filter((jr) => getPostulationCount(jr) === 0);
    }
    if (sortOrder === "most_postulations") {
      return [...list].sort((a, b) => {
        const countDiff = getPostulationCount(b) - getPostulationCount(a);
        if (countDiff !== 0) return countDiff;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
    }
    if (sortOrder === "oldest") {
      return [...list].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    }
    return [...list].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [myJobRequests, postulationFilter, sortOrder]);

  const handleSelectJobRequest = (jobRequestId: number) => {
    if (selectedId === jobRequestId) return;
    fetchMyJobRequestPostulations(jobRequestId);
  };

  if (myJobRequestsLoading) {
    return (
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={styles.container}
      >
        <div className={styles.loadingContainer}>
          <RegisteredUserProfileLoader />
        </div>
      </motion.section>
    );
  }

  if (myJobRequestsError) {
    return (
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={styles.container}
      >
        <p className={styles.error} role="alert">
          {myJobRequestsError}
        </p>
      </motion.section>
    );
  }

  if (!totalCount) {
    return (
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={styles.container}
      >
        <ProfileEmptyState
          illustration="generic"
          title="No publicaste avisos laborales"
          description="Cuando publiques una vacante en el mercado de trabajo, vas a poder ver acá los postulantes que recibas."
          primaryAction={{
            label: "Publicar vacante",
            onClick: () => navigate("/mercado-de-trabajo/crearoferta"),
          }}
        />
      </motion.section>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={styles.container}
    >
      <div className={styles.toolbar}>
        <p className={styles.toolbarSummary}>
          {postulationFilter === "all"
            ? `${totalCount} ${totalCount === 1 ? "aviso" : "avisos"}`
            : `${filteredJobRequests.length} de ${totalCount}`}
          {withPostulationsCount > 0 && (
            <span className={styles.toolbarSummaryMuted}>
              {" "}
              · {withPostulationsCount} con postulantes
            </span>
          )}
        </p>
        <div className={styles.toolbarControls}>
          <label className={styles.toolbarField}>
            <span className={styles.toolbarFieldLabel}>Mostrar</span>
            <select
              className={styles.toolbarSelect}
              value={postulationFilter}
              onChange={(e) =>
                setPostulationFilter(e.target.value as PostulationFilter)
              }
            >
              <option value="all">Todos</option>
              <option value="with">Con postulaciones</option>
              <option value="without">Sin postulaciones</option>
            </select>
          </label>
          <label className={styles.toolbarField}>
            <span className={styles.toolbarFieldLabel}>Orden</span>
            <select
              className={styles.toolbarSelect}
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as SortOrder)}
            >
              <option value="recent">Más recientes</option>
              <option value="oldest">Más antiguos</option>
              <option value="most_postulations">Más postulantes</option>
            </select>
          </label>
        </div>
      </div>

      <div className={styles.layout}>
        <div className={styles.listCard}>
          <div className={styles.list}>
            {filteredJobRequests.length === 0 ? (
              <p className={styles.listEmpty}>
                No hay avisos que coincidan con este filtro.
              </p>
            ) : (
              filteredJobRequests.map((jobRequest) => {
                const postulationCount = getPostulationCount(jobRequest);
                return (
                  <button
                    key={jobRequest.id}
                    type="button"
                    className={`${styles.listItem} ${
                      selectedId === jobRequest.id ? styles.listItemActive : ""
                    }`}
                    onClick={() => handleSelectJobRequest(jobRequest.id)}
                  >
                    <div className={styles.listItemTop}>
                      <span className={styles.itemId}>Aviso #{jobRequest.id}</span>
                      <span
                        className={`${styles.itemCount} ${
                          postulationCount > 0
                            ? styles.itemCountActive
                            : styles.itemCountEmpty
                        }`}
                      >
                        {postulationCount}{" "}
                        {postulationCount === 1 ? "postulante" : "postulantes"}
                      </span>
                    </div>
                    <p className={styles.itemDescription}>
                      {jobRequest.description || "Sin descripción"}
                    </p>
                    <div className={styles.itemMeta}>
                      <span>Publicado: {formatDate(jobRequest.createdAt)}</span>
                      <span>Vence: {formatDate(jobRequest.activeUntil)}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className={styles.detailCard}>
          {!selectedMyJobRequest ? (
            <p className={styles.detailPlaceholder}>
              Elegí un aviso de la lista para ver quién se postuló.
            </p>
          ) : (
            <>
              <div className={styles.detailHeader}>
                <h3 className={styles.cardTitle}>
                  Postulantes · aviso #{selectedMyJobRequest.id}
                </h3>
                <p className={styles.detailDescription}>
                  {selectedMyJobRequest.description || "Sin descripción"}
                </p>
                <div className={styles.detailMeta}>
                  <span>
                    Vigencia hasta {formatDate(selectedMyJobRequest.activeUntil)}
                  </span>
                  {selectedMyJobRequest.contactMethod ? (
                    <span>
                      <Mail size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />
                      {selectedMyJobRequest.contactMethod}
                      {selectedMyJobRequest.alternativeContact
                        ? `: ${selectedMyJobRequest.alternativeContact}`
                        : ""}
                    </span>
                  ) : null}
                </div>
              </div>

              {myJobRequestPostulationsLoading && (
                <p className={styles.detailLoading}>Cargando postulantes...</p>
              )}
              {!myJobRequestPostulationsLoading && myJobRequestPostulationsError && (
                <p className={styles.error} role="alert">
                  {myJobRequestPostulationsError}
                </p>
              )}
              {!myJobRequestPostulationsLoading &&
                !myJobRequestPostulationsError &&
                myJobRequestPostulations.length === 0 && (
                  <p className={styles.detailPlaceholder}>
                    Este aviso todavía no recibió postulaciones.
                  </p>
                )}
              {!myJobRequestPostulationsLoading &&
                !myJobRequestPostulationsError &&
                myJobRequestPostulations.length > 0 && (
                  <div className={styles.postulationsList}>
                    {myJobRequestPostulations.map((postulation) => (
                      <article key={postulation.id} className={styles.postulationCard}>
                        <div className={styles.postulationTop}>
                          <span className={styles.postulationId}>
                            Postulación #{postulation.id}
                          </span>
                          <span className={styles.postulationDate}>
                            {formatDateTime(postulation.appliedAt)}
                          </span>
                        </div>
                        <p className={styles.coverLetter}>
                          {postulation.coverLetter || "Sin carta de presentación."}
                        </p>
                        {postulation.cvUrl ? (
                          <a
                            href={postulation.cvUrl}
                            target="_blank"
                            rel="noreferrer"
                            className={styles.cvLink}
                          >
                            <ExternalLink size={14} />
                            Ver CV
                          </a>
                        ) : (
                          <span className={styles.cvMissing}>Sin CV adjunto</span>
                        )}
                        {postulation.applicant ? (
                          <div className={styles.applicant}>
                            <span>
                              <User size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />
                              {`${postulation.applicant.name || ""} ${postulation.applicant.last_name || ""}`.trim() ||
                                postulation.applicant.username}
                            </span>
                            {postulation.applicant.username ? (
                              <span>{postulation.applicant.username}</span>
                            ) : null}
                            {postulation.applicant.phone ? (
                              <span>{postulation.applicant.phone}</span>
                            ) : null}
                          </div>
                        ) : null}
                      </article>
                    ))}
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    </motion.section>
  );
};

const mapStateToProps = (state: any) => ({
  myJobRequests: state.profile.myJobRequests,
  myJobRequestsLoading: state.profile.myJobRequestsLoading,
  myJobRequestsError: state.profile.myJobRequestsError,
  selectedMyJobRequest: state.profile.selectedMyJobRequest,
  myJobRequestPostulations: state.profile.myJobRequestPostulations,
  myJobRequestPostulationsLoading: state.profile.myJobRequestPostulationsLoading,
  myJobRequestPostulationsError: state.profile.myJobRequestPostulationsError,
});

export default connect(mapStateToProps, {
  fetchMyJobRequests,
  fetchMyJobRequestPostulations,
  clearMyJobRequestPostulations,
})(MisAvisosLaborales);
